import { ChangeDetectionStrategy, Component, computed, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NzAutocompleteModule, NzAutocompleteOptionComponent } from 'ng-zorro-antd/auto-complete';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { Subject, catchError, of, switchMap } from 'rxjs';
import { TranslationService } from '../../../core/services/translation.service';
import { FabricVariationModel } from '../../models/fabric-variation.model';
import { CustomCurrencyPipe } from '../../pipes/custom-currency.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { DatatableService, TableKeys } from '../../services/datatable.service';
import { FabricVariationService } from '../../services/fabric-variation.service';
import { fallbackImage } from '../../utils/image.util';
import { SearchInputComponent } from '../search-input/search-input.component';

/** A letter or two matches most of the catalogue — not worth a round trip. */
const MIN_CHARS = 2;

/** Suggestions in the panel. Past this the reader is better served by the list itself. */
const SUGGESTION_LIMIT = 3;

/** `nzValue` of the footer option. Prefixed so no fabric uuid can collide with it. */
const SEE_ALL = '__see_all__';

/**
 * Fabric search with a suggestion panel: the first few matches plus a way through to the
 * full list, which is where filtering, sorting and paging live.
 *
 * Suggestions come from the same `/shop` endpoint the list reads, so a row in the panel is
 * the same row the list will show — and its `totalItems` is what tells us there is more
 * than the panel holds.
 */
@Component({
  selector: 'app-fabric-search',
  standalone: true,
  imports: [
    NzAutocompleteModule,
    NzIconModule,
    SearchInputComponent,
    CustomCurrencyPipe,
    TranslatePipe,
  ],
  templateUrl: './fabric-search.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FabricSearchComponent {
  private readonly field = viewChild(SearchInputComponent);

  readonly SEE_ALL = SEE_ALL;
  readonly fallbackImageUrl = fallbackImage;

  /**
   * Held here rather than written inline: `{{count}}` inside an interpolation reads as a
   * nested interpolation to the template parser.
   */
  readonly seeAllKey = 'See all {{count}} fabrics';

  readonly term = signal('');
  readonly results = signal<FabricVariationModel[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);

  /** The catalogue holds more than the panel shows — the only case the footer option appears. */
  readonly hasMore = computed(() => this.total() > this.results().length);

  /** A term long enough to have been asked about, so an empty panel means "no match". */
  readonly searched = computed(() => this.term().length >= MIN_CHARS);

  private readonly query$ = new Subject<string>();

  constructor(
    private readonly fabricVariationService: FabricVariationService,
    private readonly datatable: DatatableService,
    private readonly router: Router,
    protected readonly i18n: TranslationService
  ) {
    // switchMap: the field is debounced, but a slow early response must still never land on
    // top of a newer one — the panel would then be answering a term no longer in the box.
    this.query$
      .pipe(
        switchMap((term) =>
          this.fabricVariationService
            .get<FabricVariationModel>(1, SUGGESTION_LIMIT, {}, term)
            .pipe(catchError(() => of({ data: [], metadata: { totalItems: 0 } } as any)))
        ),
        takeUntilDestroyed()
      )
      .subscribe((response: any) => {
        this.results.set(response?.data ?? []);
        this.total.set(response?.metadata?.totalItems ?? 0);
        this.loading.set(false);
      });
  }

  /** For a host that reveals the field on demand — see main-layout's search band. */
  focus(): void {
    this.field()?.focus();
  }

  onSearch(term: string): void {
    this.term.set(term);

    if (term.length < MIN_CHARS) {
      this.results.set([]);
      this.total.set(0);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.query$.next(term);
  }

  /** One handler for the panel: the footer option carries the sentinel, every other row a uuid. */
  onSelect(option: NzAutocompleteOptionComponent): void {
    if (option.nzValue === SEE_ALL) {
      this.seeAll();
      return;
    }

    this.openFabric(option.nzValue);
  }

  /**
   * The fabric's page, with the list seeded so the way back out of it is the search this fabric was
   * picked from — the breadcrumb's "Fabrics", the nav link, whichever the reader takes. The same
   * thing `DatatableService` does for a reader who went through the list, for one who skipped it.
   *
   * Seeded after the navigation rather than before: the service refuses a write from a list whose
   * area is being left, and reaching this panel from the home page may well be that very case.
   */
  private openFabric(uuid: string): void {
    const term = this.term().trim();

    this.router.navigate(['/fabrics', uuid]).then((navigated) => {
      if (navigated && term) {
        this.datatable.seedSearch(TableKeys.FABRIC, term);
      }
    });
  }

  /**
   * The full list, seeded with the term — `FabricListPageComponent` reads it off the query
   * string and searches on it.
   */
  private seeAll(): void {
    const term = this.term().trim();
    this.router.navigate(['/fabrics'], { queryParams: term ? { search: term } : {} });
  }

  // ── Row price, by the same rule fabric-card applies, so the panel and the card it links
  //    to can never read differently for one fabric.

  /** No range, or a `max` at or under the €3 placeholder: not a real price. */
  isQuoteOnly(fabric: FabricVariationModel): boolean {
    const range = fabric.priceRange;
    return !range || (range.max ?? 0) <= 3;
  }

  isSinglePrice(fabric: FabricVariationModel): boolean {
    const range = fabric.priceRange;
    return !!range && range.min === range.max;
  }
}
